// The preview synth's voice, built from a patch (lib/synth.ts) on ANY
// context — the live one or an OfflineAudioContext rendering a mixdown — out
// of native nodes only, so both build it the same way, synchronously:
//
//   unison oscillators (each panned) + noise → low-pass (its own envelope,
//   key-tracked, opened by velocity) → + sub, + partial → saturation →
//   ADSR gain → out, with a send to a small room per `out`.
//
// The envelope is not styling: a raw oscillator started and stopped at full
// level clicks audibly on both edges. The release runs past the note's end,
// so the returned oscillator stops at the envelope's `end`; stopping it early
// stops every source of the voice.

import type { MidiNoteEvent } from "@web-shared/lib/audio-engine";
import { noiseBuffer, scheduleDrum } from "@web-shared/lib/drums";
import { DEFAULT_PATCH, trackedCutoff, voiceEnvelope, type SynthPatch } from "@web-shared/lib/synth";

/** The room the `space` send feeds: one per output node, so a lane's voices
 * share one reverb (and it sits before the lane's fader and mute). */
const rooms = new WeakMap<AudioNode, ConvolverNode>();
const roomImpulses = new WeakMap<BaseAudioContext, AudioBuffer>();
const shaperCurves = new Map<number, Float32Array<ArrayBuffer>>();

/** Every source of a voice, by the node that stands for it — so a caller
 * holding the primary oscillator can stop the whole voice at a time of its
 * choosing (`stopVoice`), not just the one node, and never leave a sub or
 * noise layer to its own scheduled end under a new schedule. */
const voiceSources = new WeakMap<AudioScheduledSourceNode, AudioScheduledSourceNode[]>();

/** Register a voice's sources against the node handed to callers. */
export function registerVoice(primary: AudioScheduledSourceNode, sources: readonly AudioScheduledSourceNode[]): void {
  voiceSources.set(primary, [...sources]);
}

/**
 * Stop a voice — every source of it — at `when` (now when omitted). The
 * primary's `ended` then tears the graph down as it would at the voice's
 * own end. A node that is no voice (a click, a buffer source) is simply
 * stopped.
 */
export function stopVoice(node: AudioScheduledSourceNode, when?: number): void {
  const sources = voiceSources.get(node) ?? [node];
  for (const src of sources) {
    try {
      if (when === undefined) src.stop();
      else src.stop(when);
    } catch {
      // never started, or already finished
    }
  }
}

export function scheduleTone(
  ctx: BaseAudioContext,
  note: MidiNoteEvent,
  when: number,
  out: AudioNode,
  patch: SynthPatch = DEFAULT_PATCH,
): OscillatorNode {
  if (patch.kind === "kit") return scheduleDrum(ctx, note, when, out, patch.level);
  const env = voiceEnvelope(patch, note.durationSec, note.velocity);
  const noteEnd = when + Math.max(env.decayEnd, note.durationSec);
  const stopAt = when + env.end;
  const frequency = 440 * 2 ** ((note.pitch - 69) / 12);
  const velocity = Math.max(0, Math.min(1, note.velocity / 127));
  const sources: AudioScheduledSourceNode[] = [];
  const nodes: AudioNode[] = [];
  const start = <T extends AudioScheduledSourceNode>(src: T): T => {
    src.start(when);
    src.stop(stopAt);
    sources.push(src);
    return src;
  };

  // Oscillators. Detuned copies are uncorrelated, so they sum by power:
  // 1/√n each keeps unison as loud as one oscillator.
  const count = Math.max(1, Math.round(patch.voices ?? (patch.detuneCents > 0 ? 2 : 1)));
  const pre = ctx.createGain();
  nodes.push(pre);
  const oscs: OscillatorNode[] = [];
  for (let i = 0; i < count; i++) {
    const place = count === 1 ? 0 : (i / (count - 1)) * 2 - 1;
    const osc = ctx.createOscillator();
    osc.type = patch.wave;
    osc.frequency.value = frequency;
    osc.detune.value = (place * patch.detuneCents) / 2;
    const g = ctx.createGain();
    g.gain.value = 1 / Math.sqrt(count);
    osc.connect(g);
    nodes.push(g);
    if (patch.spread && count > 1) {
      const pan = ctx.createStereoPanner();
      pan.pan.value = place * patch.spread;
      g.connect(pan);
      pan.connect(pre);
      nodes.push(pan);
    } else {
      g.connect(pre);
    }
    oscs.push(start(osc));
  }
  if (patch.noise) {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    src.loop = true;
    const g = ctx.createGain();
    g.gain.value = patch.noise;
    src.connect(g);
    g.connect(pre);
    nodes.push(g);
    start(src);
  }
  if (patch.vibrato && patch.vibrato.cents > 0) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = patch.vibrato.rateHz;
    const depth = ctx.createGain();
    const onset = when + Math.min(patch.vibrato.delaySec, note.durationSec);
    depth.gain.setValueAtTime(0, when);
    depth.gain.setValueAtTime(0, onset);
    depth.gain.linearRampToValueAtTime(patch.vibrato.cents, onset + 0.3);
    lfo.connect(depth);
    for (const osc of oscs) depth.connect(osc.detune);
    nodes.push(depth);
    start(lfo);
  }

  // Filter: opens on the attack by `filterEnv` (less for a soft note, as
  // far as `velocityToFilter` says) and falls back over `filterDecay`.
  let head: AudioNode = pre;
  if (Number.isFinite(patch.cutoffHz)) {
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.Q.value = patch.resonance;
    const rest = Math.min(trackedCutoff(patch, frequency), 20_000);
    const opening = Math.max(1, patch.filterEnv) ** (1 - (patch.velocityToFilter ?? 0) * (1 - velocity));
    const open = Math.min(rest * opening, 20_000);
    filter.frequency.setValueAtTime(open, when);
    if (open > rest) approach(filter.frequency, open, rest, when + env.attackEnd, Math.max(patch.filterDecay ?? patch.decay, 0.01) / 3);
    head.connect(filter);
    head = filter;
    nodes.push(filter);
  }

  // Sub and partial join after the filter, so the cutoff never thins them.
  const post = ctx.createGain();
  head.connect(post);
  nodes.push(post);
  if (patch.sub) {
    const sub = ctx.createOscillator();
    sub.frequency.value = frequency / 2;
    const g = ctx.createGain();
    g.gain.value = patch.sub;
    sub.connect(g);
    g.connect(post);
    nodes.push(g);
    start(sub);
  }
  if (patch.partial && frequency * patch.partial.ratio < 18_000) {
    const tine = ctx.createOscillator();
    tine.frequency.value = frequency * patch.partial.ratio;
    const g = ctx.createGain();
    const level = patch.partial.level * velocity;
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(level, when + 0.002);
    approach(g.gain, level, 0, when + 0.002, patch.partial.decaySec / 3);
    tine.connect(g);
    g.connect(post);
    nodes.push(g);
    start(tine);
  }
  head = post;
  if (patch.drive) {
    const shaper = ctx.createWaveShaper();
    shaper.curve = saturationCurve(patch.drive);
    shaper.oversample = "2x";
    head.connect(shaper);
    head = shaper;
    nodes.push(shaper);
  }

  // Amp: a linear attack, exponential decay and release — how a struck or
  // plucked thing falls, and no corner where the release starts.
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, when);
  amp.gain.linearRampToValueAtTime(env.peak, when + env.attackEnd);
  let held = env.peak;
  if (env.decayEnd > env.attackEnd) {
    const tau = (env.decayEnd - env.attackEnd) / 3;
    approach(amp.gain, env.peak, env.sustainLevel, when + env.attackEnd, tau);
    held = env.sustainLevel + (env.peak - env.sustainLevel) * Math.exp(-(noteEnd - when - env.attackEnd) / tau);
  }
  approach(amp.gain, held, 0, noteEnd, (stopAt - noteEnd) / 5);
  head.connect(amp);
  amp.connect(out);
  nodes.push(amp);
  if (patch.space) {
    const send = ctx.createGain();
    send.gain.value = patch.space;
    amp.connect(send);
    send.connect(room(ctx, out));
    nodes.push(send);
  }

  const primary = oscs[0]!;
  // addEventListener, not `onended` — stopVoices/stopAudition null `onended`
  // to silence state callbacks, and that must not orphan the chain.
  primary.addEventListener("ended", () => {
    // Whatever ended the voice — its scheduled stop, or a caller's
    // osc.stop() — every source goes with it; only `primary` is stopped by
    // name.
    for (const src of sources) {
      if (src === primary) continue;
      try {
        src.stop();
      } catch {
        // already stopped
      }
      src.disconnect();
    }
    for (const n of nodes) n.disconnect();
  });
  registerVoice(primary, sources);
  return primary;
}

/** An exponential approach from `from` to `to`, starting at `at` — anchored
 * by a set value there, so the curve starts from a known level whatever
 * came before it (node-web-audio-api otherwise applies a target before its
 * start time). */
function approach(param: AudioParam, from: number, to: number, at: number, tau: number): void {
  param.setValueAtTime(from, at);
  param.setTargetAtTime(to, at, tau);
}

/** tanh, normalised so ±1 still maps to ±1: louder parts round off, quiet
 * ones pass nearly straight. */
function saturationCurve(drive: number): Float32Array<ArrayBuffer> {
  const k = 1 + Math.max(0, Math.min(1, drive)) * 5;
  let curve = shaperCurves.get(k);
  if (curve) return curve;
  curve = new Float32Array(1025);
  for (let i = 0; i < curve.length; i++) {
    const x = (i / (curve.length - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x) / Math.tanh(k);
  }
  shaperCurves.set(k, curve);
  return curve;
}

/** The send's room for `out`, made on first use: a convolver into `out`. */
function room(ctx: BaseAudioContext, out: AudioNode): ConvolverNode {
  let c = rooms.get(out);
  if (c) return c;
  c = ctx.createConvolver();
  c.buffer = roomImpulse(ctx);
  c.connect(out);
  rooms.set(out, c);
  return c;
}

/** A small, dark room: 1.6 s of decorrelated stereo noise falling 60 dB,
 * each sample a little duller than the last. Seeded, like `noiseBuffer`,
 * so a render is repeatable. */
function roomImpulse(ctx: BaseAudioContext): AudioBuffer {
  let buf = roomImpulses.get(ctx);
  if (buf) return buf;
  const seconds = 1.6;
  const length = Math.round(seconds * ctx.sampleRate);
  buf = ctx.createBuffer(2, length, ctx.sampleRate);
  let seed = 0x2545f491;
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    let smooth = 0;
    for (let i = 0; i < length; i++) {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      const white = (seed >>> 0) / 0x80000000 - 1;
      const t = i / length;
      // The low-pass closes as the tail goes on: air takes the top first.
      const pole = 0.2 + 0.7 * t;
      smooth = smooth * pole + white * (1 - pole);
      data[i] = smooth * 10 ** ((-60 * t) / 20);
    }
  }
  roomImpulses.set(ctx, buf);
  return buf;
}
